import { Component, Input, OnDestroy, OnInit, output, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormControl, FormsModule, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslocoModule } from '@ngneat/transloco';
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'timeconverter-component',
    templateUrl: './timeconverter.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, MatFormFieldModule, MatInputModule, ReactiveFormsModule, TranslocoModule]
})
export class TimeConverterComponent implements OnInit, OnDestroy {

    @Input() control: FormControl<number>;
    @Input() label: string;
    @Input() hint: string;
    @Input() placeholder: string;
    @Input() isUpdate = false;
    @Input() useSummaryView = false;
    @Input() readonly = false;
    @Input() required = false;
    @Input() hidden = false;
    @Input() admitsNegative = false;
    @Input() format: 'full' | 'hours' = 'full';
    @Input() markAsTouched: Subject<void>;

    onBlur = output<void>();

    controlOriginal: FormControl;
    defaultValue: string;

    private _unsubscribeAll = new Subject<void>();

    constructor() { }

    ngOnInit() {
        const originalValue: string = this.control.value !== null && this.control.value !== undefined ?
            this.minutesToString(this.control.value) : '';

        const validators: ValidatorFn[] = [
            (control: AbstractControl) => {
                if (this.required) {
                    if (this.control.invalid) {
                        return { required: true };
                    }
                } else {
                    return null;
                }
            },
            Validators.pattern(this.admitsNegative ? /(^-?(\s+)?[0-9]+(d)((\s+[0-9]+(h))?(\s+[0-9]+(m)((\s+)?$)|(\s+)?$)|(\s+)?$))|(^-?(\s+)?[0-9]+(h)(\s+[0-9]+(m)((\s+)?$)|(\s+)?$))|(^-?(\s+)?[0-9]+(m)((\s+)?$))/
                : /(^[0-9]+(d)((\s+[0-9]+(h))?(\s+[0-9]+(m)((\s+)?$)|(\s+)?$)|(\s+)?$))|(^[0-9]+(h)(\s+[0-9]+(m)((\s+)?$)|(\s+)?$))|(^[0-9]+(m)((\s+)?$))/)
        ];

        this.controlOriginal = new FormControl(originalValue, validators);
        if (this.control.disabled || this.readonly) {
            this.controlOriginal.disable();
        } else {
            this.control.addValidators((control: AbstractControl) => {
                if (this.controlOriginal.value && control.value === undefined) {
                    return { invalidTime: true }
                } else {
                    return null;
                }
            });
        }
        this.defaultValue = this.controlOriginal.value;

        this.controlOriginal.valueChanges
            .pipe(
                takeUntil(this._unsubscribeAll),
                distinctUntilChanged()
            )
            .subscribe((value) => {
                if (value === '') {
                    this.control.setValue(undefined);
                } else {
                    let totalMinutes = 0;
                    const negative = value.startsWith('-');
                    const timeData = value.split(' ');
                    if (!this.controlOriginal.errors?.pattern) {
                        for (const t of timeData) {
                            if (t.includes('d')) {
                                const days = Number(t.split('d')[0]);
                                totalMinutes += Math.abs(days) * 24 * 60;
                            } else if (t.includes('h')) {
                                const hours = Number(t.split('h')[0]);
                                totalMinutes += Math.abs(hours) * 60;
                            } else if (t.includes('m')) {
                                const minutes = Number(t.split('m')[0]);
                                totalMinutes += Math.abs(minutes);
                            }
                        }
                    } else {
                        totalMinutes = undefined;
                    }
                    this.control.setValue(negative && totalMinutes ? -totalMinutes : totalMinutes);
                }
            });

        this.control.valueChanges
            .pipe(
                takeUntil(this._unsubscribeAll),
                distinctUntilChanged()
            )
            .subscribe((value) => {
                if (value !== undefined) {
                    this.controlOriginal.setValue(this.minutesToString(value), { emitEvent: false });
                }
            });

        if (this.markAsTouched) {
            this.markAsTouched.pipe(takeUntil(this._unsubscribeAll))
                .subscribe(() => this.controlOriginal.markAllAsTouched());
        }
    }

    ngOnDestroy() {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    private minutesToString(minutes: number) {
        let str = minutes < 0 && this.admitsNegative ? '-' : '';
        minutes = Math.abs(minutes);
        const days = Math.floor(minutes / 1440);
        const hours = this.format === 'full' ? Math.floor((minutes % 1440) / 60) : Math.floor(minutes / 60);
        minutes = minutes % 60;
        if (days > 0 && this.format === 'full') {
            str = str + String(days) + 'd ';
        }
        if (hours > 0) {
            str = str + String(hours) + 'h ';
        }
        if (minutes > 0) {
            str = str + String(minutes) + 'm';
        }
        if (str === '') {
            return '0m';
        } else {
            return str;
        }
    }

}
